import { createClient } from '@supabase/supabase-js';
import { supabase } from './supabase';

const supabaseUrl = import.meta.env.VITE_PUBLIC_SUPABASE_URL || 'https://bwrjzklcyhddlpzbjlgd.supabase.co';
const supabaseServiceRoleKey = import.meta.env.VITE_SUPABASE_SERVICE_ROLE_KEY;

export interface User {
  id: string;
  email: string;
  created_at: string;
  email_confirmed_at: string | null;
  last_sign_in_at: string | null;
  banned_until: string | null;
  isActive: boolean;
  role?: string; // Role do usuário (admin, user, etc)
  codes_copied_count?: number; // Contador de códigos copiados pelo usuário
  cards_created_count?: number; // Contador de cards criados pelo usuário
  points?: number; // Pontos totais do usuário
  level?: number; // Nível do usuário
}

export interface UserProfile {
  user_id: string;
  email: string;
  role: string;
  cards_created_count: number;
  codes_copied_count: number;
  points: number;
  level: number;
  badges: Badge[];
}

export interface Badge {
  id: string;
  badge_type: string;
  badge_name: string;
  badge_description: string | null;
  earned_at: string;
}

export interface RankingEntry {
  user_id: string;
  email: string;
  cards_created_count: number;
  codes_copied_count: number;
  points: number;
  level: number;
  ranking_position: number;
}

// Singleton para o admin client - reutiliza a mesma instância
let adminClientInstance: ReturnType<typeof createClient> | null = null;

/**
 * Cria um cliente admin usando service_role_key
 * Nota: Esta chave deve estar apenas no backend em produção
 * Usa singleton para evitar múltiplas instâncias
 */
function getAdminClient() {
  if (!supabaseServiceRoleKey) {
    throw new Error('Service role key não configurada. Use uma Edge Function para operações admin.');
  }
  
  // Reutilizar instância existente se já foi criada
  if (!adminClientInstance) {
    adminClientInstance = createClient(supabaseUrl, supabaseServiceRoleKey, {
      auth: {
        autoRefreshToken: false,
        persistSession: false
      }
    });
  }
  
  return adminClientInstance;
}

/**
 * Lista todos os usuários do sistema
 */
export async function listUsers(): Promise<User[]> {
  try {
    // Se não tiver service_role_key, usar Edge Function ou RPC
    if (!supabaseServiceRoleKey) {
      // Fallback: usar função RPC se disponível
      const { data, error } = await supabase.rpc('list_all_users');
      if (error) throw error;
      
      // Mapear os dados retornados pela RPC para o formato User
      return (data || []).map((user: any) => {
        // Verificar se o usuário está banido
        const now = new Date();
        const bannedUntil = user.banned_until ? new Date(user.banned_until) : null;
        const isActive = !bannedUntil || bannedUntil <= now;
        
        return {
          id: user.id,
          email: user.email || '',
          created_at: user.created_at,
          email_confirmed_at: user.email_confirmed_at,
          last_sign_in_at: user.last_sign_in_at,
          banned_until: user.banned_until,
          isActive: isActive,
          role: user.role || 'user',
          codes_copied_count: user.codes_copied_count || 0,
          cards_created_count: user.cards_created_count || 0,
          points: user.points || 0,
          level: user.level || 1
        };
      });
    }

    const adminClient = getAdminClient();
    const { data, error } = await adminClient.auth.admin.listUsers();
    
    if (error) throw error;
    
    // Buscar roles e stats da tabela user_profiles
    const userIds = (data.users || []).map(u => u.id);
    const { data: profiles, error: profilesError } = await supabase
      .from('user_profiles')
      .select('user_id, role, codes_copied_count, cards_created_count, points, level')
      .in('user_id', userIds);
    
    // Criar mapas de user_id -> stats
    const roleMap = new Map<string, string>();
    const codesCopiedMap = new Map<string, number>();
    const cardsCreatedMap = new Map<string, number>();
    const pointsMap = new Map<string, number>();
    const levelMap = new Map<string, number>();
    if (profiles && !profilesError) {
      profiles.forEach(profile => {
        roleMap.set(profile.user_id, profile.role);
        codesCopiedMap.set(profile.user_id, profile.codes_copied_count || 0);
        cardsCreatedMap.set(profile.user_id, profile.cards_created_count || 0);
        pointsMap.set(profile.user_id, profile.points || 0);
        levelMap.set(profile.user_id, profile.level || 1);
      });
    }
    
    return (data.users || []).map(user => {
      // Verificar se o usuário está banido
      // Um usuário está ativo se banned_until é null OU se banned_until está no passado (ban expirado)
      const now = new Date();
      const bannedUntil = user.banned_until ? new Date(user.banned_until) : null;
      const isActive = !bannedUntil || bannedUntil <= now;
      
      return {
        id: user.id,
        email: user.email || '',
        created_at: user.created_at,
        email_confirmed_at: user.email_confirmed_at,
        last_sign_in_at: user.last_sign_in_at,
        banned_until: user.banned_until,
        isActive: isActive,
        role: roleMap.get(user.id) || 'user',
        codes_copied_count: codesCopiedMap.get(user.id) || 0,
        cards_created_count: cardsCreatedMap.get(user.id) || 0,
        points: pointsMap.get(user.id) || 0,
        level: levelMap.get(user.id) || 1
      };
    });
  } catch (error: any) {
    console.error('Erro ao listar usuários:', error);
    throw new Error(error.message || 'Erro ao listar usuários');
  }
}

/**
 * Cria um novo usuário
 * Usa signup do Supabase Auth + atualização de role
 */
export async function createUser(email: string, password: string, role: string = 'user'): Promise<User> {
  try {
    // Tentar usar Admin API se service_role_key estiver disponível
    if (supabaseServiceRoleKey) {
      const adminClient = getAdminClient();
      const { data, error } = await adminClient.auth.admin.createUser({
        email,
        password,
        email_confirm: true
      });
      
      if (error) throw error;
      if (!data.user) throw new Error('Usuário não foi criado');
      
      await new Promise(resolve => setTimeout(resolve, 500));
      
      if (role !== 'user') {
        await updateUserRole(data.user.id, role);
      }
      
      return {
        id: data.user.id,
        email: data.user.email || '',
        created_at: data.user.created_at,
        email_confirmed_at: data.user.email_confirmed_at,
        last_sign_in_at: data.user.last_sign_in_at,
        banned_until: data.user.banned_until,
        isActive: !data.user.banned_until,
        role: role
      };
    }
    
    // Salvar sessão atual do admin
    const { data: { session: currentSession } } = await supabase.auth.getSession();
    
    // Usar signup + update role como fallback
    const { data: signUpData, error: signUpError } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: window.location.origin,
        data: {
          created_by_admin: true
        }
      }
    });
    
    if (signUpError) throw signUpError;
    if (!signUpData.user) throw new Error('Erro ao criar usuário');
    
    // Restaurar sessão do admin imediatamente
    if (currentSession) {
      await supabase.auth.setSession({
        access_token: currentSession.access_token,
        refresh_token: currentSession.refresh_token
      });
    }
    
    // Aguardar o trigger criar o perfil
    await new Promise(resolve => setTimeout(resolve, 1000));
    
    // Atualizar o role se necessário
    if (role !== 'user') {
      await updateUserRole(signUpData.user.id, role);
    }
    
    return {
      id: signUpData.user.id,
      email: signUpData.user.email || '',
      created_at: signUpData.user.created_at,
      email_confirmed_at: signUpData.user.email_confirmed_at,
      last_sign_in_at: null,
      banned_until: null,
      isActive: true,
      role: role
    };
  } catch (error: any) {
    console.error('Erro ao criar usuário:', error);
    throw new Error(error.message || 'Erro ao criar usuário');
  }
}

/**
 * Atualiza o role de um usuário na tabela user_profiles
 */
export async function updateUserRole(userId: string, role: string): Promise<void> {
  try {
    // Verificar se o perfil existe
    const { data: existingProfile } = await supabase
      .from('user_profiles')
      .select('id')
      .eq('user_id', userId)
      .single();

    if (existingProfile) {
      // Atualizar perfil existente
      const { error } = await supabase
        .from('user_profiles')
        .update({ 
          role: role,
          updated_at: new Date().toISOString()
        })
        .eq('user_id', userId);

      if (error) throw error;
    } else {
      // Criar perfil se não existir
      const { error } = await supabase
        .from('user_profiles')
        .insert({
          user_id: userId,
          role: role
        });

      if (error) throw error;
    }
  } catch (error: any) {
    console.error('Erro ao atualizar role do usuário:', error);
    throw new Error(error.message || 'Erro ao atualizar role do usuário');
  }
}

/**
 * Atualiza a senha de um usuário
 */
export async function updateUserPassword(userId: string, newPassword: string): Promise<void> {
  try {
    if (!supabaseServiceRoleKey) {
      throw new Error('Service role key não configurada. Use uma Edge Function para atualizar senhas.');
    }

    const adminClient = getAdminClient();
    const { error } = await adminClient.auth.admin.updateUserById(userId, {
      password: newPassword
    });
    
    if (error) throw error;
  } catch (error: any) {
    console.error('Erro ao atualizar senha:', error);
    throw new Error(error.message || 'Erro ao atualizar senha');
  }
}

/**
 * Inativa um usuário (ban)
 */
export async function banUser(userId: string): Promise<void> {
  try {
    // Calcular data de banimento (100 anos no futuro)
    const banUntil = new Date();
    banUntil.setFullYear(banUntil.getFullYear() + 100);
    const banUntilISO = banUntil.toISOString();
    
    console.log('Tentando banir usuário:', { userId, banUntilISO });
    
    // Usar função RPC para banir (mais confiável que Admin API)
    const { error: rpcError } = await supabase.rpc('ban_user', {
      p_user_id: userId,
      p_ban_until: banUntilISO
    });
    
    if (rpcError) {
      console.error('Erro RPC ao banir usuário:', rpcError);
      
      // Fallback: tentar Admin API se RPC falhar
      if (supabaseServiceRoleKey) {
        console.log('Tentando Admin API como fallback...');
        const adminClient = getAdminClient();
        
        // Tentar ban_duration primeiro
        let adminSuccess = false;
        try {
          const { data: updateData, error: banError } = await adminClient.auth.admin.updateUserById(userId, {
            ban_duration: '100y'
          });
          
          if (banError) {
            console.warn('ban_duration falhou:', banError);
            // Tentar banned_until
            const { error: untilError } = await adminClient.auth.admin.updateUserById(userId, {
              banned_until: banUntilISO
            });
            
            if (untilError) {
              throw untilError;
            } else {
              adminSuccess = true;
            }
          } else {
            adminSuccess = true;
          }
        } catch (adminError: any) {
          console.error('Admin API também falhou:', adminError);
          throw new Error(`Erro ao banir usuário: ${rpcError.message || adminError.message || 'Erro desconhecido'}`);
        }
        
        if (!adminSuccess) {
          throw new Error(`Erro ao banir usuário: ${rpcError.message || 'Erro desconhecido'}`);
        }
      } else {
        throw new Error(`Erro ao banir usuário: ${rpcError.message || 'Erro desconhecido'}`);
      }
    }
    
    // Verificar se o banimento foi aplicado
    await new Promise(resolve => setTimeout(resolve, 500));
    
    // Verificar via query direta
    const { data: userData, error: verifyError } = await supabase
      .from('user_profiles')
      .select('user_id')
      .eq('user_id', userId)
      .single();
    
    if (verifyError) {
      console.warn('Não foi possível verificar banimento via user_profiles:', verifyError);
    }
    
    console.log('Banimento aplicado com sucesso para usuário:', userId);
  } catch (error: any) {
    console.error('Erro ao banir usuário:', error);
    throw new Error(error.message || 'Erro ao banir usuário');
  }
}

/**
 * Ativa um usuário (remove ban)
 */
export async function unbanUser(userId: string): Promise<void> {
  try {
    console.log('Tentando desbanir usuário:', userId);
    
    // Usar função RPC para desbanir (mais confiável que Admin API)
    const { error: rpcError } = await supabase.rpc('unban_user', {
      p_user_id: userId
    });
    
    if (rpcError) {
      console.error('Erro RPC ao desbanir usuário:', rpcError);
      
      // Fallback: tentar Admin API se RPC falhar
      if (supabaseServiceRoleKey) {
        console.log('Tentando Admin API como fallback...');
        const adminClient = getAdminClient();
        
        const { error: adminError } = await adminClient.auth.admin.updateUserById(userId, {
          banned_until: null
        });
        
        if (adminError) {
          console.error('Admin API também falhou:', adminError);
          throw new Error(`Erro ao desbanir usuário: ${rpcError.message || adminError.message || 'Erro desconhecido'}`);
        }
      } else {
        throw new Error(`Erro ao desbanir usuário: ${rpcError.message || 'Erro desconhecido'}`);
      }
    }
    
    console.log('Usuário desbanido com sucesso:', userId);
  } catch (error: any) {
    console.error('Erro ao ativar usuário:', error);
    throw new Error(error.message || 'Erro ao ativar usuário');
  }
}

/**
 * Incrementa o contador de códigos copiados de um usuário
 * Usa função RPC para incremento atômico
 */
export async function incrementCodeCopyCount(userId: string): Promise<void> {
  try {
    // #region agent log
    fetch('http://127.0.0.1:7242/ingest/de71e05c-d9c0-4fcc-bf2e-a43ef0ae691a',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'admin.ts:441',message:'incrementCodeCopyCount iniciado',data:{userId},timestamp:Date.now(),sessionId:'debug-session',hypothesisId:'H1,H3'})}).catch(()=>{});
    // #endregion
    // Usar função RPC para incremento atômico e atualizar pontos
    const { data: rpcData, error } = await supabase.rpc('update_points_on_copy', {
      user_id_param: userId
    });
    // #region agent log
    fetch('http://127.0.0.1:7242/ingest/de71e05c-d9c0-4fcc-bf2e-a43ef0ae691a',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'admin.ts:447',message:'Resultado RPC update_points_on_copy',data:{hasError:!!error,errorMsg:error?.message,errorCode:error?.code,rpcData:rpcData},timestamp:Date.now(),sessionId:'debug-session',hypothesisId:'H1,H6'})}).catch(()=>{});
    // #endregion
    
    if (error) {
      // #region agent log
      fetch('http://127.0.0.1:7242/ingest/de71e05c-d9c0-4fcc-bf2e-a43ef0ae691a',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'admin.ts:450',message:'Erro na RPC, tentando fallback',data:{error:error?.message},timestamp:Date.now(),sessionId:'debug-session',hypothesisId:'H1'})}).catch(()=>{});
      // #endregion
      // Fallback: usar função antiga se a nova não existir
      const { error: oldError } = await supabase.rpc('increment_user_code_copy_count', {
        user_id_param: userId
      });
      // #region agent log
      fetch('http://127.0.0.1:7242/ingest/de71e05c-d9c0-4fcc-bf2e-a43ef0ae691a',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'admin.ts:453',message:'Resultado RPC fallback increment_user_code_copy_count',data:{hasError:!!oldError,errorMsg:oldError?.message,errorCode:oldError?.code},timestamp:Date.now(),sessionId:'debug-session',hypothesisId:'H1'})}).catch(()=>{});
      // #endregion
      
      if (oldError) {
        // Se nenhuma função RPC existir, fazer update direto
        console.warn('RPC functions não disponíveis, usando update direto:', oldError);
        // #region agent log
        fetch('http://127.0.0.1:7242/ingest/de71e05c-d9c0-4fcc-bf2e-a43ef0ae691a',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'admin.ts:456',message:'Buscando perfil do usuário',data:{userId},timestamp:Date.now(),sessionId:'debug-session',hypothesisId:'H4'})}).catch(()=>{});
        // #endregion
        
        const { data: profile } = await supabase
          .from('user_profiles')
          .select('codes_copied_count, points')
          .eq('user_id', userId)
          .single();
        // #region agent log
        fetch('http://127.0.0.1:7242/ingest/de71e05c-d9c0-4fcc-bf2e-a43ef0ae691a',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'admin.ts:463',message:'Perfil carregado',data:{hasProfile:!!profile,currentCount:profile?.codes_copied_count,currentPoints:profile?.points},timestamp:Date.now(),sessionId:'debug-session',hypothesisId:'H4'})}).catch(()=>{});
        // #endregion
        
        const currentCount = profile?.codes_copied_count || 0;
        const currentPoints = profile?.points || 0;
        const newPoints = currentPoints + 1; // 1 ponto por código copiado
        const newLevel = Math.max(1, Math.floor((newPoints / 100) + 1));
        // #region agent log
        fetch('http://127.0.0.1:7242/ingest/de71e05c-d9c0-4fcc-bf2e-a43ef0ae691a',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'admin.ts:468',message:'Tentando atualizar perfil',data:{currentCount,newCount:currentCount+1,currentPoints,newPoints,newLevel},timestamp:Date.now(),sessionId:'debug-session',hypothesisId:'H4'})}).catch(()=>{});
        // #endregion
        
        const { error: updateError } = await supabase
          .from('user_profiles')
          .update({ 
            codes_copied_count: currentCount + 1,
            points: newPoints,
            level: newLevel,
            updated_at: new Date().toISOString()
          })
          .eq('user_id', userId);
        // #region agent log
        fetch('http://127.0.0.1:7242/ingest/de71e05c-d9c0-4fcc-bf2e-a43ef0ae691a',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'admin.ts:478',message:'Resultado do update',data:{hasError:!!updateError,errorMsg:updateError?.message,errorCode:updateError?.code},timestamp:Date.now(),sessionId:'debug-session',hypothesisId:'H4'})}).catch(()=>{});
        // #endregion
        
        if (updateError) {
          // #region agent log
          fetch('http://127.0.0.1:7242/ingest/de71e05c-d9c0-4fcc-bf2e-a43ef0ae691a',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'admin.ts:480',message:'Update falhou, tentando insert',data:{},timestamp:Date.now(),sessionId:'debug-session',hypothesisId:'H4'})}).catch(()=>{});
          // #endregion
          const { error: insertError } = await supabase
            .from('user_profiles')
            .insert({
              user_id: userId,
              codes_copied_count: 1,
              points: 1,
              level: 1,
              role: 'user'
            });
          // #region agent log
          fetch('http://127.0.0.1:7242/ingest/de71e05c-d9c0-4fcc-bf2e-a43ef0ae691a',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'admin.ts:490',message:'Resultado do insert',data:{hasError:!!insertError,errorMsg:insertError?.message,errorCode:insertError?.code},timestamp:Date.now(),sessionId:'debug-session',hypothesisId:'H4'})}).catch(()=>{});
          // #endregion
          
          if (insertError) {
            console.error('Erro ao incrementar contador de códigos copiados:', insertError);
          }
        }
      }
    }
    
    // #region agent log
    fetch('http://127.0.0.1:7242/ingest/de71e05c-d9c0-4fcc-bf2e-a43ef0ae691a',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'admin.ts:496',message:'incrementCodeCopyCount completado com sucesso',data:{},timestamp:Date.now(),sessionId:'debug-session',hypothesisId:'H1,H3,H4',runId:'post-fix'})}).catch(()=>{});
    // #endregion
    
    // #region agent log - Verificar se o update realmente aconteceu
    const { data: verifyProfile } = await supabase
      .from('user_profiles')
      .select('codes_copied_count, points, level')
      .eq('user_id', userId)
      .single();
    fetch('http://127.0.0.1:7242/ingest/de71e05c-d9c0-4fcc-bf2e-a43ef0ae691a',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'admin.ts:503',message:'Verificação pós-incremento',data:{codesCopied:verifyProfile?.codes_copied_count,points:verifyProfile?.points,level:verifyProfile?.level},timestamp:Date.now(),sessionId:'debug-session',hypothesisId:'H6',runId:'post-fix'})}).catch(()=>{});
    // #endregion
    
    // Verificar e atribuir badges
    try {
      await supabase.rpc('check_and_assign_badges', {
        user_id_param: userId
      });
    } catch (err) {
      // Erro silencioso - badges são opcionais
      console.warn('Erro ao verificar badges:', err);
    }
  } catch (error: any) {
    // #region agent log
    fetch('http://127.0.0.1:7242/ingest/de71e05c-d9c0-4fcc-bf2e-a43ef0ae691a',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'admin.ts:507',message:'ERRO CRÍTICO no incrementCodeCopyCount',data:{error:error?.message,errorStack:error?.stack},timestamp:Date.now(),sessionId:'debug-session',hypothesisId:'H3'})}).catch(()=>{});
    // #endregion
    // Erro silencioso - não afetar a experiência do usuário
    console.error('Erro ao incrementar contador de códigos copiados:', error);
  }
}

/**
 * Incrementa o contador de cards criados de um usuário
 * Usa função RPC para incremento atômico e atualiza pontos/nível
 */
export async function incrementCardsCreatedCount(userId: string): Promise<void> {
  try {
    // Usar função RPC para incremento atômico
    const { error } = await supabase.rpc('increment_cards_created_count', {
      user_id_param: userId
    });
    
    if (error) {
      console.warn('RPC function não disponível, usando update direto:', error);
      
      const { data: profile } = await supabase
        .from('user_profiles')
        .select('cards_created_count, points')
        .eq('user_id', userId)
        .single();
      
      const currentCount = profile?.cards_created_count || 0;
      const currentPoints = profile?.points || 0;
      const newPoints = currentPoints + 10; // 10 pontos por card criado
      const newLevel = Math.max(1, Math.floor((newPoints / 100) + 1));
      
      const { error: updateError } = await supabase
        .from('user_profiles')
        .update({ 
          cards_created_count: currentCount + 1,
          points: newPoints,
          level: newLevel,
          updated_at: new Date().toISOString()
        })
        .eq('user_id', userId);
      
      if (updateError) {
        const { error: insertError } = await supabase
          .from('user_profiles')
          .insert({
            user_id: userId,
            cards_created_count: 1,
            points: 10,
            level: 1,
            role: 'user'
          });
        
        if (insertError) {
          console.error('Erro ao incrementar contador de cards criados:', insertError);
        }
      }
    }
    
    // Verificar e atribuir badges
    await supabase.rpc('check_and_assign_badges', {
      user_id_param: userId
    });
  } catch (error: any) {
    console.error('Erro ao incrementar contador de cards criados:', error);
  }
}

/**
 * Busca perfil completo do usuário incluindo stats de gamificação
 */
export async function getUserProfile(userId: string): Promise<UserProfile | null> {
  try {
    // Buscar perfil do usuário
    const { data: profile, error: profileError } = await supabase
      .from('user_profiles')
      .select('user_id, role, cards_created_count, codes_copied_count, points, level')
      .eq('user_id', userId)
      .single();
    
    if (profileError || !profile) {
      // Se não existir, criar perfil básico
      const { data: newProfile, error: createError } = await supabase
        .from('user_profiles')
        .insert({
          user_id: userId,
          role: 'user',
          cards_created_count: 0,
          codes_copied_count: 0,
          points: 0,
          level: 1
        })
        .select()
        .single();
      
      if (createError || !newProfile) {
        return null;
      }
      
      // Buscar email
      const email = await getUserEmail(userId);
      
      // Buscar badges
      const { data: badges } = await supabase
        .from('user_badges')
        .select('*')
        .eq('user_id', userId)
        .order('earned_at', { ascending: false });
      
      return {
        user_id: newProfile.user_id,
        email: email || '',
        role: newProfile.role || 'user',
        cards_created_count: newProfile.cards_created_count || 0,
        codes_copied_count: newProfile.codes_copied_count || 0,
        points: newProfile.points || 0,
        level: newProfile.level || 1,
        badges: (badges || []).map(b => ({
          id: b.id,
          badge_type: b.badge_type,
          badge_name: b.badge_name,
          badge_description: b.badge_description,
          earned_at: b.earned_at
        }))
      };
    }
    
    // Buscar email
    const email = await getUserEmail(userId);
    
    // Buscar badges
    const { data: badges } = await supabase
      .from('user_badges')
      .select('*')
      .eq('user_id', userId)
      .order('earned_at', { ascending: false });
    
    return {
      user_id: profile.user_id,
      email: email || '',
      role: profile.role || 'user',
      cards_created_count: profile.cards_created_count || 0,
      codes_copied_count: profile.codes_copied_count || 0,
      points: profile.points || 0,
      level: profile.level || 1,
      badges: (badges || []).map(b => ({
        id: b.id,
        badge_type: b.badge_type,
        badge_name: b.badge_name,
        badge_description: b.badge_description,
        earned_at: b.earned_at
      }))
    };
  } catch (error: any) {
    console.error('Erro ao buscar perfil do usuário:', error);
    return null;
  }
}

/**
 * Busca ranking de criadores
 */
export async function getRanking(limit: number = 100): Promise<RankingEntry[]> {
  try {
    const { data, error } = await supabase.rpc('get_creators_ranking', {
      limit_count: limit
    });
    
    if (error) {
      console.error('Erro ao buscar ranking:', error);
      return [];
    }
    
    return (data || []).map((entry: any) => ({
      user_id: entry.user_id,
      email: entry.email || '',
      cards_created_count: entry.cards_created_count || 0,
      codes_copied_count: entry.codes_copied_count || 0,
      points: entry.points || 0,
      level: entry.level || 1,
      ranking_position: entry.ranking_position || 0
    }));
  } catch (error: any) {
    console.error('Erro ao buscar ranking:', error);
    return [];
  }
}

/**
 * Obtém o email de um usuário pelo ID
 * Prioriza RPC para evitar criar instância admin desnecessariamente
 */
export async function getUserEmail(userId: string): Promise<string | null> {
  try {
    // Priorizar função RPC (não cria instância admin)
    const { data, error } = await supabase.rpc('get_user_email', { user_id: userId });
    
    if (!error && data) {
      return data;
    }
    
    // Fallback: usar admin client apenas se RPC falhar e service_role_key estiver disponível
    if (supabaseServiceRoleKey) {
      try {
        const adminClient = getAdminClient();
        const { data: userData, error: userError } = await adminClient.auth.admin.getUserById(userId);
        if (!userError && userData?.user?.email) {
          return userData.user.email;
        }
      } catch (adminError: any) {
        console.warn('Erro ao buscar email via admin client:', adminError);
      }
    }
    
    return null;
  } catch (error: any) {
    console.error('Erro ao obter email do usuário:', error);
    return null;
  }
}

